import { useEffect, useState } from "react";
import { EventPaymentNotice } from "./EventPaymentNotice";
import { EventRegistrationForm } from "./EventRegistrationForm";
import { isJointEventsHost, KATRIN_SITE_URL, RAYK_SITE_URL } from "./lib/bookingHost";
import { KATRIN_RAYK_PORTRAIT_PATH, KATRIN_RAYK_PORTRAIT_PNG_PATH } from "./lib/portrait";
import {
  eventHostsById,
  formatEventHosts,
  formatEventMode,
  formatEventSchedule,
  formatLocation,
  normalizePublicEvents,
  PUBLIC_EVENT_SELECT,
  type PublicEvent,
  type PublicEventHostCatalog,
  publicEventsListView,
} from "./lib/publicEvents";
import { getSupabaseClient } from "./lib/supabase";
import "./event-registration.css";
import "./public-events.css";
import "./joint-events-brand.css";

export function PublicEvents({ eventSlug }: { eventSlug?: string }) {
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const isLive = status === "ready";
  const listView = publicEventsListView({ status, events, eventSlug });

  useEffect(() => {
    let isMounted = true;

    async function loadEvents() {
      try {
        const supabase = getSupabaseClient();
        let query = supabase
          .from("events")
          .select(PUBLIC_EVENT_SELECT)
          .eq("status", "published")
          .eq("is_active", true)
          .gt("ends_at", new Date().toISOString())
          .order("starts_at", { ascending: true });
        if (eventSlug) query = query.eq("slug", eventSlug);

        const [eventResult, hostResult] = await Promise.all([
          query,
          supabase.rpc("public_event_catalog"),
        ]);

        if (eventResult.error) throw eventResult.error;
        if (hostResult.error) {
          console.warn("Event hosts could not be loaded.", hostResult.error);
        }

        const hostsByEventId = eventHostsById(
          (hostResult.data ?? null) as PublicEventHostCatalog | null,
        );
        const enrichedEvents = ((eventResult.data ?? []) as Array<Omit<PublicEvent, "hosts">>).map(
          (event) => ({
            ...event,
            hosts: hostsByEventId.get(event.id) ?? [],
          }),
        );

        if (isMounted) {
          const normalizedEvents = normalizePublicEvents(enrichedEvents);
          setEvents(normalizedEvents);
          setStatus("ready");
        }
      } catch (error) {
        console.error("Published events could not be loaded.", error);
        if (isMounted) {
          setEvents([]);
          setStatus("error");
        }
      }
    }

    setStatus("loading");
    void loadEvents();

    return () => {
      isMounted = false;
    };
  }, [eventSlug]);

  const isJoint = isJointEventsHost();

  return (
    <section
      className={isJoint ? "public-events public-events--joint" : "public-events"}
      id="veranstaltungen"
      aria-labelledby="events-title"
    >
      {isJoint ? (
        <div className="public-events__brand-header">
          <a
            className="public-events__brand"
            href="/"
            aria-label="Katrin & Rayk · Buchung – Veranstaltungsübersicht"
          >
            <img src="/kr-logo.png" width="48" height="48" alt="" />
            <span>
              <strong>Katrin &amp; Rayk</strong>
              <small>Buchung</small>
            </span>
          </a>
          <a className="public-events__website-link" href="https://katrinundrayk.de/">
            <span aria-hidden="true">↗</span> Zur Website
          </a>
        </div>
      ) : null}
      <header className={!eventSlug && isJoint ? "public-events__hero--with-portrait" : undefined}>
        <div className="public-events__header-copy">
          <p>{eventSlug ? "Veranstaltung" : "Veranstaltungen"}</p>
          <h2 id="events-title">
            {eventSlug
              ? "Hier weißt du, worauf du dich einlässt."
              : "Manches verändert sich erst, wenn wir es gemeinsam in den Raum stellen."}
          </h2>
          <span>
            {eventSlug
              ? "Worum es geht, wann und wo wir uns treffen und wie du dich anmeldest – klar und verbindlich."
              : isJoint
                ? "Veranstaltungen von Katrin & Rayk. Wähle einen Termin und öffne dann Details und Anmeldung."
                : "Aufstellungen, Teachings, Tadschalli und gemeinsame Erfahrungsräume. Wähle einen Termin und öffne erst dann die Details und Anmeldung."}
          </span>
          {eventSlug ? (
            <a className="public-events__back" href="/veranstaltungen">
              Alle Veranstaltungen
            </a>
          ) : null}
          {!eventSlug && isJoint ? (
            <nav className="public-events__practitioner-links" aria-label="Einzelseiten">
              <a className="public-events__back" href={RAYK_SITE_URL}>
                1:1 bei Rayk
              </a>
              <a className="public-events__back" href={KATRIN_SITE_URL}>
                1:1 bei Katrin
              </a>
            </nav>
          ) : null}
        </div>
        {!eventSlug && isJoint ? (
          <figure className="public-events__portrait">
            <picture>
              <source srcSet={KATRIN_RAYK_PORTRAIT_PATH} type="image/webp" />
              <img
                src={KATRIN_RAYK_PORTRAIT_PNG_PATH}
                alt="Katrin und Rayk Schörling"
                width="766"
                height="990"
                decoding="async"
              />
            </picture>
          </figure>
        ) : null}
      </header>

      <EventPaymentNotice />

      {listView === "error" ? (
        <div className="public-events__payment-notice is-error" role="alert">
          <strong>Veranstaltungen konnten gerade nicht geladen werden.</strong>
          <span>Bitte öffne die Seite in einigen Minuten erneut.</span>
        </div>
      ) : null}

      <div className={`public-events__list ${eventSlug ? "is-detail" : "is-overview"}`}>
        {listView === "loading" ? (
          <article className="public-event" role="status">
            <p>Veröffentlichte Termine</p>
            <h3>Die nächsten Veranstaltungen werden geladen …</h3>
            <span>Einen Moment – wir holen die veröffentlichten Abende.</span>
          </article>
        ) : null}

        {events.map((event) =>
          eventSlug ? (
            <article className="public-event public-event--featured" key={event.id}>
              <p>{event.eyebrow ?? formatEventSchedule(event)}</p>
              <h3>{event.title}</h3>
              <span>{event.summary}</span>

              {event.description ? (
                <div className="public-event__description">
                  {event.description
                    .split(/\n{2,}/)
                    .map((paragraph) => paragraph.trim())
                    .filter(Boolean)
                    .map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                </div>
              ) : null}

              <dl>
                <div>
                  <dt>Zeit</dt>
                  <dd>{formatEventSchedule(event)}</dd>
                </div>
                <div>
                  <dt>Leitung</dt>
                  <dd>{formatEventHosts(event)}</dd>
                </div>
                <div>
                  <dt>Ort</dt>
                  <dd>{formatLocation(event) || "Ort folgt"}</dd>
                </div>
                <div>
                  <dt>Format</dt>
                  <dd>{formatEventMode(event)}</dd>
                </div>
              </dl>

              <EventRegistrationForm
                eventId={event.id}
                eventTitle={event.title}
                isLive={isLive}
                roles={event.event_roles}
                allowCashPayment={event.slug === "tajalli-intensivgruppe-2026-11"}
              />
            </article>
          ) : (
            <article className="public-event-card" key={event.id}>
              <div className="public-event-card__topline">
                <span>{formatEventSchedule(event)}</span>
                <small>{formatEventMode(event)}</small>
              </div>

              <div className="public-event-card__body">
                <h3>{event.title}</h3>
                <p>{event.summary}</p>

                <div className="public-event-card__meta">
                  <span>
                    <small>Leitung</small>
                    <strong>{formatEventHosts(event)}</strong>
                  </span>
                  <span>
                    <small>Ort</small>
                    <strong>{formatLocation(event) || "Ort folgt"}</strong>
                  </span>
                </div>
              </div>

              <a
                className="public-event-card__open"
                href={`/veranstaltungen/${encodeURIComponent(event.slug)}`}
              >
                Details & anmelden
                <span aria-hidden="true">→</span>
              </a>
            </article>
          ),
        )}

        {listView === "empty" ? (
          <article className="public-event public-event--preview">
            <p>Neue Termine</p>
            <h3>Die nächsten Veranstaltungen entstehen gerade.</h3>
            <span>Sobald ein Termin steht, findest du hier Inhalt, Ort, Preis und Anmeldung.</span>
            <div className="public-event__status" role="status">
              <span>In Vorbereitung</span>
              <small>Keine provisorischen Preise oder Termine werden angezeigt.</small>
            </div>
          </article>
        ) : null}

        {listView === "missing" ? (
          <article className="public-event public-event--preview">
            <p>Nicht öffentlich</p>
            <h3>Diese Veranstaltung ist gerade nicht sichtbar.</h3>
            <span>Alle aktuell offenen Veranstaltungen findest du in der Übersicht.</span>
            <a className="public-events__back" href="/veranstaltungen">
              Alle Veranstaltungen
            </a>
          </article>
        ) : null}
      </div>
    </section>
  );
}
